import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import { urlNameSegment } from '../../../utils/slugify';
import {
  AdminHeaderAction,
  AdminRowMenuItem,
  AdminPageTitle,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import {
  WF_ROUTE,
  buildSampleWorkflowRules,
  formatWorkflowConditionsDisplay,
  formatWorkflowTriggerDisplay,
  getWorkflowModuleLabel,
  getRecordTypeLabel,
  getStatusLabel,
  sortWorkflowRules,
} from '../../../data/workflowConfig';

const TABLE_COLUMNS = [
  'Rule ID',
  'Rule Name',
  'Module',
  'Record Type',
  'Current Status',
  'Next Status',
  'Trigger',
  'Conditions',
  'Actions',
];

const TABLE_GRID =
  'minmax(0, 0.7fr) minmax(0, 1.15fr) minmax(0, 0.85fr) minmax(0, 0.95fr) minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1.4fr) minmax(0, 0.85fr) minmax(62px, auto)';

const Workflow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rules, setRules] = useState(() => sortWorkflowRules(buildSampleWorkflowRules()));

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.DOUBLE);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedRules,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(rules);

  useEffect(() => {
    const incoming = location.state?.rules;
    if (Array.isArray(incoming)) {
      setRules(sortWorkflowRules(incoming));
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const openRuleView = (rule) => {
    navigate(`${WF_ROUTE}/${urlNameSegment(rule.ruleName)}`, {
      state: { rule, viewMode: true, existingRules: rules },
    });
  };

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <AdminPageTitle className="!mb-0">Workflow</AdminPageTitle>
          <AdminHeaderAction onClick={() => navigate(`${WF_ROUTE}/add`, { state: { existingRules: rules } })}>
            Add Rule
          </AdminHeaderAction>
        </div>

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <div
              className="grid w-full min-w-[1280px] py-3 text-offblack border-b border-lightgray px-4 gap-x-3 gap-y-2"
              style={{ gridTemplateColumns: TABLE_GRID }}
            >
              {TABLE_COLUMNS.map((label) => (
                <div
                  key={label}
                  className={`flex items-center min-w-0${label === 'Actions' ? ' shrink-0 justify-center' : ''}`}
                >
                  <AdminTableHeader className={label === 'Actions' ? '' : 'truncate'}>{label}</AdminTableHeader>
                </div>
              ))}
            </div>

            {paginatedRules.map((rule) => {
              const triggerText = formatWorkflowTriggerDisplay(rule);
              const conditionsText = formatWorkflowConditionsDisplay(rule);
              return (
                <div
                  key={rule.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openRuleView(rule)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openRuleView(rule);
                    }
                  }}
                  className="grid w-full min-w-[1280px] py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-3 gap-y-2 cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primaryblue focus-visible:ring-offset-1"
                  style={{ gridTemplateColumns: TABLE_GRID }}
                >
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableMeta className="truncate block" title={rule.id}>
                      {rule.id}
                    </AdminTableMeta>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={rule.ruleName}>
                      {rule.ruleName}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">{getWorkflowModuleLabel(rule.module)}</AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">
                      {getRecordTypeLabel(rule.module, rule.recordType) || '—'}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">
                      {getStatusLabel(rule.recordType, rule.currentStatus) || '—'}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">
                      {getStatusLabel(rule.recordType, rule.nextStatus) || '—'}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={triggerText}>
                      {triggerText}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={conditionsText}>
                      {conditionsText}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center justify-center shrink-0 overflow-visible" onClick={(e) => e.stopPropagation()}>
                    <div
                      className="relative inline-flex"
                      ref={(el) => {
                        rowActionsRefs.current[rule.id] = el;
                      }}
                    >
                      <AdminRowActionsButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRowActions(rule.id);
                        }}
                        aria-expanded={!!rowActionsOpen[rule.id]}
                        title="Actions"
                        aria-label="Row actions"
                      >
                        <AdminRowMenuIcon />
                      </AdminRowActionsButton>
                      {rowActionsOpen[rule.id] && rowActionsMenuLayout && (
                        <AdminFixedRowActionsMenu
                          layout={rowActionsMenuLayout}
                          menuRef={rowActionsMenuRef}
                          placementReady={rowActionsMenuPlacementReady}
                        >
                          <AdminRowMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              closeRowActions();
                              openRuleView(rule);
                            }}
                          >
                            View
                          </AdminRowMenuItem>
                          <AdminRowMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              closeRowActions();
                              navigate(`${WF_ROUTE}/${urlNameSegment(rule.ruleName)}/edit`, {
                                state: { rule, existingRules: rules },
                              });
                            }}
                          >
                            Edit
                          </AdminRowMenuItem>
                        </AdminFixedRowActionsMenu>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default Workflow;
